import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { InvoiceConfig } from '../../types';
import { formatCents, formatTaxRateBp } from '../money';
import {
    CreditNoteBetraege,
    FreelancerCreditNote,
    FreelancerCreditNoteLine,
    FreelancerSnapshot,
    GUTSCHRIFT_TITEL,
    KLEINUNTERNEHMER_HINWEIS,
    STORNO_TITEL_PRAEFIX,
    faelligAm,
    istKleinunternehmer,
} from '../freelancerCreditNoteLogic';
import { COLORS, formatDate } from './pdfUtils';
import {
    A4_GEOMETRY,
    CompanyInfo,
    cityFromAddress,
    createLetterhead,
    createSpaceGuard,
    drawFooters,
    drawPage1Header,
    longDate,
} from './letterhead';
import { ensureEmbeddedFont, registerEmbeddedFont } from './embeddedFont';

/**
 * Gutschrift-PDF (Gutschriftverfahren §14 Abs. 2 Satz 2 UStG) — Phase D5.
 *
 * ─── Wer ist hier wer ──────────────────────────────────────────────────────
 * Anders als bei jedem anderen Beleg dieses Hauses sind wir NICHT der
 * Leistende. GEFAS ist der Leistungsempfaenger und stellt den Beleg ueber die
 * Leistung des FREELANCERS aus. Auf dem Papier heisst das:
 *
 *   • Briefkopf/Fusszeile = GEFAS (Aussteller — wie auf jedem Brief)
 *   • Anschriftfeld       = der Freelancer (Leistender UND Empfaenger dieses
 *                           Belegs), aus dem eingefrorenen Snapshot
 *   • Steuernummer/USt-IdNr. im Belegkopf = die des FREELANCERS
 *     (§14 Abs. 4 Nr. 2 UStG bezieht sich auf den Leistenden)
 *   • Umsatzsteuer nach SEINEM Status: 19 % oder, bei Kleinunternehmern nach
 *     §19 UStG, gar keine — dann traegt der Beleg den Pflichthinweis.
 *   • Die IBAN ist SEINE: wir zahlen, er bekommt.
 *
 * Das Wort „Gutschrift" steht woertlich auf dem Beleg — es ist eine
 * Pflichtangabe, keine Ueberschrift, die sich schoener formulieren liesse
 * (freelancerCreditNoteLogic.GUTSCHRIFT_TITEL).
 *
 * Nutzt dieselbe Briefkopf- und Briefpapier-Mechanik wie Rechnung und Angebot
 * (letterhead.ts): „gleiches Briefpapier" ist damit keine Frage der Pflege
 * dreier Layouts, sondern folgt aus der geteilten Implementierung.
 *
 * Die Bausteine unterhalb (Kopfzeilen, Tabellenzeilen, Summenblock, Hinweise)
 * sind PUR und einzeln getestet — kein jsPDF, kein DOM. Sie beantworten die
 * Fragen, bei denen ein Fehler auf dem Papier steht; das Zeichnen darunter ist
 * nur noch Anordnung.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Pure Bausteine
// ═══════════════════════════════════════════════════════════════════════════

/** Anschriftfeld: der Freelancer aus dem eingefrorenen Snapshot. */
export const empfaengerZeilen = (snapshot: FreelancerSnapshot | null | undefined): string[] => {
    const s = snapshot || {};
    const zeilen: string[] = [];
    // Firma zuerst, wenn es eine gibt: der Beleg geht an das Unternehmen, die
    // Person steht darunter. Ohne Firma traegt die Person die erste Zeile.
    if (s.firma?.trim()) zeilen.push(s.firma.trim());
    if (s.name?.trim()) zeilen.push(s.name.trim());
    if (s.street?.trim()) zeilen.push(s.street.trim());
    const ort = [s.zip, s.city].map((v) => (v || '').trim()).filter(Boolean).join(' ');
    if (ort) zeilen.push(ort);
    return zeilen;
};

/**
 * Der rechte Block mit den Pflichtangaben.
 *
 * Reihenfolge = Suchreihenfolge einer Buchhaltung: Nummer, Datum, Zeitraum,
 * Faelligkeit, dann die Steuermerkmale des Leistenden.
 *
 * `originalNummer` wird nur beim Storno-Beleg gesetzt — dann steht dort, auf
 * welchen Beleg er sich bezieht. Ohne diese Zeile waere ein Beleg mit
 * negativen Betraegen nicht zuzuordnen.
 */
export const kopfZeilen = (
    beleg: Pick<
        FreelancerCreditNote,
        | 'nummer'
        | 'ausgestelltAm'
        | 'leistungszeitraumVon'
        | 'leistungszeitraumBis'
        | 'zahlungszielTage'
        | 'stornoOf'
    >,
    snapshot: FreelancerSnapshot | null | undefined,
    originalNummer?: string | null,
): Array<[string, string]> => {
    const zeilen: Array<[string, string]> = [];
    if (beleg.nummer) zeilen.push(['Gutschrift-Nr.', beleg.nummer]);
    if (beleg.ausgestelltAm) zeilen.push(['Datum', formatDate(beleg.ausgestelltAm)]);
    if (beleg.stornoOf && originalNummer) {
        zeilen.push(['Storno zu', originalNummer]);
    }
    if (beleg.leistungszeitraumVon && beleg.leistungszeitraumBis) {
        zeilen.push([
            'Leistungszeitraum',
            beleg.leistungszeitraumVon === beleg.leistungszeitraumBis
                ? formatDate(beleg.leistungszeitraumBis)
                : `${formatDate(beleg.leistungszeitraumVon)} – ${formatDate(beleg.leistungszeitraumBis)}`,
        ]);
    } else if (beleg.leistungszeitraumBis) {
        zeilen.push(['Leistungsdatum', formatDate(beleg.leistungszeitraumBis)]);
    }
    // Kein Faelligkeitsdatum auf dem Storno: dort ist nichts zu zahlen, und
    // die Angabe liest sich als Zahlungsfrist (gleiche Regel wie im
    // Rechnungsgenerator).
    if (!beleg.stornoOf) {
        const faellig = faelligAm(beleg.ausgestelltAm, beleg.zahlungszielTage);
        if (faellig) zeilen.push(['Zahlung bis', formatDate(faellig)]);
    }
    // Steuermerkmale des LEISTENDEN — §14 Abs. 4 Nr. 2 UStG.
    if (snapshot?.ustIdnr?.trim()) zeilen.push(['USt-IdNr.', snapshot.ustIdnr.trim()]);
    else if (snapshot?.steuernummer?.trim()) {
        zeilen.push(['Steuernummer', snapshot.steuernummer.trim()]);
    }
    return zeilen;
};

/**
 * Eine Zeile der Positionstabelle: Projekt, Leistungsdatum, Betrag.
 *
 * Bewusst nur drei Spalten. Menge, Einheit und Einzelpreis gibt es hier nicht:
 * abgerechnet wird PRO BEGEHUNG zu einem vereinbarten Satz, und eine
 * Mengenspalte, in der immer „1" steht, ist Rauschen.
 */
export const positionsZeile = (
    line: Pick<FreelancerCreditNoteLine, 'descriptionSnapshot' | 'serviceDate' | 'netCents'>,
): [string, string, string] => [
    line.descriptionSnapshot || '',
    formatDate(line.serviceDate),
    formatCents(line.netCents),
];

export interface SummenZeile {
    label: string;
    wert: string;
    /** Die Schlusszeile wird fett und eine Spur groesser gesetzt. */
    hervorgehoben?: boolean;
}

/**
 * Der Summenblock.
 *
 * Beim Regelsatz drei Zeilen (netto, USt, brutto), beim Kleinunternehmer zwei
 * — eine Zeile „zzgl. 0 % USt  0,00 €" waere bei §19 sachlich falsch: es gibt
 * dort keine Umsatzsteuer, nicht eine in Hoehe von null. Der Pflichthinweis
 * steht darunter als eigener Absatz (`hinweisZeilen`).
 */
export const summenZeilen = (
    betraege: CreditNoteBetraege,
    steuersatzBp: number,
): SummenZeile[] => {
    const zeilen: SummenZeile[] = [
        { label: 'Nettobetrag', wert: formatCents(betraege.nettoCents) },
    ];
    if (!istKleinunternehmer(steuersatzBp)) {
        zeilen.push({
            label: `zzgl. ${formatTaxRateBp(steuersatzBp)} USt`,
            wert: formatCents(betraege.ustCents),
        });
    }
    zeilen.push({
        label: 'Gutschriftbetrag',
        wert: formatCents(betraege.bruttoCents),
        hervorgehoben: true,
    });
    return zeilen;
};

/**
 * Die Pflicht- und Erlaeuterungshinweise unter dem Summenblock.
 *
 * Reihenfolge ist Absicht: erst die steuerliche Aussage (§19), dann die
 * Erlaeuterung des Verfahrens (§14 Abs. 2 Satz 2), zuletzt der Storno-Vermerk.
 * Ein Beleg, dessen erste Zeile den Storno nennt, verdeckt die Steuerfrage.
 */
export const hinweisZeilen = (
    beleg: Pick<FreelancerCreditNote, 'stornoOf' | 'stornoGrund'>,
    steuersatzBp: number,
    originalNummer?: string | null,
): string[] => {
    const zeilen: string[] = [];
    if (istKleinunternehmer(steuersatzBp)) zeilen.push(KLEINUNTERNEHMER_HINWEIS);
    zeilen.push(
        'Abrechnung im Gutschriftverfahren nach § 14 Abs. 2 Satz 2 UStG. '
            + 'Widerspricht der Empfänger dieser Gutschrift, verliert sie ihre '
            + 'Wirkung als Rechnung.',
    );
    if (beleg.stornoOf) {
        zeilen.push(
            `Dieser Beleg hebt die Gutschrift ${originalNummer || ''}`.trim()
                + ' vollständig auf.'
                + (beleg.stornoGrund?.trim() ? ` Grund: ${beleg.stornoGrund.trim()}` : ''),
        );
    }
    return zeilen;
};

/** Zahlungshinweis mit der IBAN des Freelancers — nie auf dem Storno. */
export const zahlungsZeilen = (
    beleg: Pick<FreelancerCreditNote, 'stornoOf' | 'ausgestelltAm' | 'zahlungszielTage'>,
    snapshot: FreelancerSnapshot | null | undefined,
): string[] => {
    if (beleg.stornoOf) return [];
    const zeilen: string[] = [];
    const faellig = faelligAm(beleg.ausgestelltAm, beleg.zahlungszielTage);
    zeilen.push(
        faellig
            ? `Der Gutschriftbetrag wird bis zum ${formatDate(faellig)} auf das folgende Konto überwiesen:`
            : 'Der Gutschriftbetrag wird auf das folgende Konto überwiesen:',
    );
    const iban = snapshot?.iban?.trim();
    // Ohne IBAN wird KEINE erfunden und der Satz nicht stillschweigend
    // weggelassen: der Empfaenger soll sehen, dass die Angabe fehlt, und sie
    // nachreichen.
    zeilen.push(iban ? `IBAN ${iban}` : 'IBAN: im Stammsatz nicht hinterlegt');
    return zeilen;
};

/** Titel des Belegs — „Gutschrift" bzw. „Storno zur Gutschrift GS-…". */
export const belegUeberschrift = (
    beleg: Pick<FreelancerCreditNote, 'stornoOf' | 'nummer'>,
    originalNummer?: string | null,
): string => {
    if (beleg.stornoOf) {
        return `${STORNO_TITEL_PRAEFIX}${originalNummer ? ` ${originalNummer}` : ''}`;
    }
    return beleg.nummer ? `${GUTSCHRIFT_TITEL} Nr. ${beleg.nummer}` : GUTSCHRIFT_TITEL;
};

// ═══════════════════════════════════════════════════════════════════════════
// Zeichnen
// ═══════════════════════════════════════════════════════════════════════════

export interface CreditNotePdfInput {
    beleg: FreelancerCreditNote;
    lines: FreelancerCreditNoteLine[];
    /** Aussteller = GEFAS. Gleiche Herkunft wie beim Rechnungs-PDF. */
    company: CompanyInfo;
    /**
     * Bewusst NUR die Grussformel — der Rest von InvoiceConfig gehoert hier
     * nicht hin, obwohl der Aufrufer die ganze Konfiguration in der Hand hat.
     *
     * InvoiceConfig beschreibt GEFAS als Leistenden. Auf dieser Gutschrift ist
     * GEFAS aber der Leistungsempfaenger (siehe Kopfkommentar), und die
     * steuerlich relevanten Angaben sind die des FREELANCERS:
     *
     *   • smallBusinessNote waere GEFAS' §19-Hinweis auf einem Beleg, dessen
     *     §19-Frage am Freelancer haengt. Der richtige Hinweis steht bereits
     *     drauf: hinweisZeilen setzt KLEINUNTERNEHMER_HINWEIS aus
     *     beleg.steuersatzBp. Beides zusammen behauptete zwei Steuerstatus.
     *   • paymentNoteText/defaultPaymentTerms sind Zahlungsbedingungen fuer
     *     eingehendes Geld. Hier zahlt GEFAS aus — zahlungsZeilen bildet das
     *     mit der IBAN des Freelancers ab.
     *   • defaultHeaderText/defaultIntroText sind Rechnungstexte ("berechnen
     *     wir wie folgt") und stuenden auf einer Gutschrift falsch herum.
     *
     * Die Grussformel ist die einzige Angabe, die sich rein auf den Aussteller
     * bezieht — deshalb steht sie hier allein.
     */
    config?: Pick<InvoiceConfig, 'closingText'>;
    /** Nummer des stornierten Belegs — nur beim Storno gesetzt. */
    originalNummer?: string | null;
    userName?: string;
}

export const generateCreditNotePDF = (
    input: CreditNotePdfInput,
    opts?: { __docFactory?: () => jsPDF },
): jsPDF => {
    const { beleg, lines, company } = input;
    const geo = A4_GEOMETRY;
    const { margin, contentWidth, rightX } = geo;

    const doc = opts?.__docFactory
        ? opts.__docFactory()
        : new jsPDF({ format: 'a4', unit: 'mm', compress: true });

    // Eingebettete Schrift registrieren, bevor gezeichnet wird — gleiche
    // Begruendung wie im Rechnungsgenerator (PDF/A-Faehigkeit).
    registerEmbeddedFont(doc);

    const [tp0, tp1, tp2] = COLORS.text.primary;
    const [ln0, ln1, ln2] = COLORS.section.line;

    const lh = createLetterhead(doc, company, geo);
    lh.stampCurrentBg();
    const { addressFieldY, rightColBottom } = drawPage1Header(doc, company, lh, geo);

    const snapshot = beleg.freelancerSnapshot || {};

    // ── Anschriftfeld: der Freelancer ─────────────────────────────────────
    let addrY = addressFieldY + 5;
    const empfaenger = empfaengerZeilen(snapshot);
    doc.setTextColor(tp0, tp1, tp2);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    const ersteZeile = doc.splitTextToSize(empfaenger[0] || '', 85) as string[];
    doc.text(ersteZeile, margin, addrY);
    addrY += ersteZeile.length * 5;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    for (const l of empfaenger.slice(1)) {
        doc.text(l, margin, addrY);
        addrY += 5;
    }

    // ── Rechter Block ─────────────────────────────────────────────────────
    const metaRows = kopfZeilen(beleg, snapshot, input.originalNummer);
    let metaY = Math.max(addressFieldY + 5, rightColBottom + 6);
    const metaLabelDefaultX = rightX - 60;
    const metaLabelMinX = margin + 90;
    doc.setFontSize(9);

    // EINE Spaltenkante fuer alle Zeilen, bestimmt von der breitesten
    // Kombination — wortgleiche Mechanik wie im Rechnungs-PDF, damit die
    // beiden Belegarten nebeneinander gleich aussehen.
    const neededWidth = metaRows.reduce((widest, [label, value]) => {
        doc.setFont('helvetica', 'normal');
        const labelWidth = doc.getTextWidth(label);
        doc.setFont('helvetica', 'bold');
        return Math.max(widest, labelWidth + doc.getTextWidth(value) + 4);
    }, 0);
    const metaLabelX = Math.max(metaLabelMinX, Math.min(metaLabelDefaultX, rightX - neededWidth));

    for (const [label, value] of metaRows) {
        doc.setFont('helvetica', 'normal');
        doc.text(label, metaLabelX, metaY);
        doc.setFont('helvetica', 'bold');
        doc.text(value, rightX, metaY, { align: 'right' });
        metaY += 4.6;
    }

    // ── Ort und Datum ─────────────────────────────────────────────────────
    const companyCity = cityFromAddress(company.address);
    const datumsZeile = companyCity
        ? `${companyCity}, ${longDate(beleg.ausgestelltAm)}`
        : longDate(beleg.ausgestelltAm);

    let y = Math.max(addrY + 6, metaY + 6, addressFieldY + 30, rightColBottom + 8);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11.5);
    doc.text(datumsZeile, rightX, y, { align: 'right' });
    y += 12;

    const ensureSpace = createSpaceGuard(lh, geo, () => y, (v) => { y = v; });

    // ── Ueberschrift ──────────────────────────────────────────────────────
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    for (const l of doc.splitTextToSize(
        belegUeberschrift(beleg, input.originalNummer),
        contentWidth,
    ) as string[]) {
        doc.text(l, margin, y);
        y += 6;
    }
    y += 4;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    for (const l of doc.splitTextToSize(
        'für die von Ihnen durchgeführten und geprüften Baustellenbegehungen rechnen wir wie folgt ab:',
        contentWidth,
    ) as string[]) {
        doc.text(l, margin, y);
        y += 5;
    }
    y += 5;

    // ── Positionstabelle ──────────────────────────────────────────────────
    const body = lines.map(positionsZeile);
    autoTable(doc, {
        startY: y,
        head: [['Projekt / Begehung', 'Leistungsdatum', 'Betrag']],
        body: body.length ? body : [['—', '', formatCents(0)]],
        theme: 'plain',
        showHead: 'everyPage',
        margin: lh.autoTableMargin(),
        willDrawPage: lh.willDrawPage,
        headStyles: { textColor: [tp0, tp1, tp2], fontSize: 8, fontStyle: 'bold', lineWidth: 0 },
        styles: {
            fontSize: 8.5,
            textColor: [tp0, tp1, tp2],
            cellPadding: { top: 2.2, bottom: 2.2, left: 1.5, right: 1.5 },
            lineWidth: 0,
            // Nichts wird abgeschnitten: ein verschluckter Rest waere auf einem
            // festgeschriebenen Beleg eine falsche Angabe.
            overflow: 'linebreak',
        },
        columnStyles: {
            0: { cellWidth: 'auto' },
            1: { cellWidth: 30, halign: 'right' },
            2: { cellWidth: 30, halign: 'right', fontStyle: 'bold' },
        },
        didParseCell: (data) => {
            if (data.section === 'head' && data.column.index >= 1) {
                data.cell.styles.halign = 'right';
            }
        },
        didDrawCell: (data) => {
            // Genau EINE Linie je Zeile, gezeichnet an der letzten Spalte —
            // sonst laege sie dreifach uebereinander.
            if (data.column.index !== data.table.columns.length - 1) return;
            const lineY = data.cell.y + data.cell.height;
            doc.setDrawColor(ln0, ln1, ln2);
            doc.setLineWidth(data.section === 'head' ? 0.3 : 0.1);
            doc.line(margin, lineY, rightX, lineY);
        },
    });

    y = ((doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? y) + 6;

    // ── Summenblock ───────────────────────────────────────────────────────
    const labelX = margin + 55;
    const sumColRight = rightX - 2;
    const zeilen = summenZeilen(
        {
            nettoCents: beleg.nettoCents,
            ustCents: beleg.ustCents,
            bruttoCents: beleg.bruttoCents,
        },
        beleg.steuersatzBp,
    );
    zeilen.forEach((z, i) => {
        ensureSpace(25);
        if (z.hervorgehoben) {
            doc.setDrawColor(tp0, tp1, tp2);
            doc.setLineWidth(0.4);
            doc.line(labelX, y - 4.5, sumColRight, y - 4.5);
        }
        doc.setFont('helvetica', z.hervorgehoben ? 'bold' : 'normal');
        doc.setFontSize(z.hervorgehoben ? 11 : 10);
        doc.setTextColor(tp0, tp1, tp2);
        doc.text(z.label, labelX, y);
        doc.text(z.wert, sumColRight, y, { align: 'right' });
        y += z.hervorgehoben ? 7 : 6;
        if (i === zeilen.length - 1) y += 2;
    });

    // ── Zahlungshinweis ───────────────────────────────────────────────────
    const zahlung = zahlungsZeilen(beleg, snapshot);
    if (zahlung.length) {
        y += 4;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9.5);
        for (const z of zahlung) {
            for (const l of doc.splitTextToSize(z, contentWidth) as string[]) {
                ensureSpace(20);
                doc.text(l, margin, y);
                y += 4.5;
            }
        }
    }

    // ── Pflichthinweise ───────────────────────────────────────────────────
    const hinweise = hinweisZeilen(beleg, beleg.steuersatzBp, input.originalNummer);
    if (hinweise.length) {
        y += 4;
        doc.setFontSize(9);
        hinweise.forEach((h, i) => {
            // Der §19-Hinweis ist eine Pflichtangabe und steht fett; die
            // Erlaeuterung des Verfahrens ist Fliesstext.
            doc.setFont('helvetica', i === 0 && istKleinunternehmer(beleg.steuersatzBp) ? 'bold' : 'normal');
            for (const l of doc.splitTextToSize(h, contentWidth) as string[]) {
                ensureSpace(20);
                doc.text(l, margin, y);
                y += 4.5;
            }
            y += 1;
        });
    }

    // ── Gruss ─────────────────────────────────────────────────────────────
    // Leere Grussformel heisst "ohne GRUSSZEILE", nicht "ohne Zeichnung" —
    // dieselbe Regel wie bei Angebot und Rechnung, aus derselben Pflegestelle.
    const closing = (input.config?.closingText ?? 'Mit freundlichem Gruß').trim();
    y += 6;
    ensureSpace(30);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(tp0, tp1, tp2);
    if (closing) {
        doc.text(closing, margin, y);
        y += 10;
    } else {
        y += 6;
    }
    if (input.userName?.trim()) {
        doc.setFont('helvetica', 'bold');
        doc.text(input.userName.trim(), margin, y);
        y += 5;
    }
    if (company.name) {
        doc.setFont('helvetica', 'normal');
        doc.text(company.name, margin, y);
    }

    drawFooters(doc, company, lh, geo);
    return doc;
};

/** Bequemer Adapter fuer den Upload-Pfad (freelancerCreditNoteService). */
export const renderCreditNotePdfBytes = async (
    input: CreditNotePdfInput,
): Promise<Uint8Array> => {
    // Erst laden, dann rendern: registerEmbeddedFont ist synchron, weil es
    // mitten im Zeichnen aufgerufen wird.
    await ensureEmbeddedFont();
    return new Uint8Array(generateCreditNotePDF(input).output('arraybuffer'));
};
